import React, { useState } from 'react';
import { Box, Stack, Typography, Chip, Alert, Button, CircularProgress } from '@mui/material';
import { CheckCircle as CheckIcon, Lock as LockIcon, Info as InfoIcon, AutoAwesome as AIIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

/**
 * Shared header used by all 10 roadmap step pages.
 * Replaces the repetitive Paper+Stack+h4+Checkbox pattern.
 */
export default function StepPageHeader({
  stepNum,
  title,
  intro,
  isComplete,
  canComplete,
  onToggleComplete,
  readOnly = false,
  requireMessage = '',
  onAISuggest
}) {
  const { t } = useTranslation();
  const [aiLoading, setAiLoading] = useState(false);

  const handleAISuggest = async () => {
    if (!onAISuggest) return;
    setAiLoading(true);
    try {
      await onAISuggest();
    } finally {
      setAiLoading(false);
    }
  };

  return (
    <Box
      sx={{
        position: 'relative',
        bgcolor: 'background.paper',
        border: '1px solid var(--line-1)',
        borderRadius: 2,
        overflow: 'hidden',
        mb: 3
      }}
    >
      {/* Left accent bar */}
      <Box
        sx={{
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: 3,
          bgcolor: isComplete ? '#22c55e' : '#6366f1',
          transition: 'background-color 0.3s ease'
        }}
      />

      <Box sx={{ pl: { xs: 2.5, md: 3.5 }, pr: { xs: 2, md: 3 }, pt: 3, pb: 2.5 }}>
        {/* Read-only banner */}
        {readOnly && (
          <Alert
            severity="info"
            icon={<LockIcon fontSize="small" />}
            sx={{ mb: 2.5, py: 0.5, fontSize: '0.82rem' }}
          >
            <strong>View Only:</strong> You have read-only access to this roadmap.
          </Alert>
        )}

        {/* Title row */}
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
          spacing={2}
        >
          {/* Step number + title */}
          <Stack direction="row" alignItems="center" spacing={2}>
            {/* Circular step badge */}
            <Box
              sx={{
                width: 38,
                height: 38,
                borderRadius: '50%',
                flexShrink: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: isComplete ? 'rgba(34,197,94,0.15)' : 'rgba(99,102,241,0.15)',
                border: `1.5px solid ${isComplete ? 'rgba(34,197,94,0.4)' : 'rgba(99,102,241,0.4)'}`,
                color: isComplete ? 'var(--status-green)' : 'var(--accent-fg)',
                transition: 'all 0.3s ease'
              }}
            >
              {isComplete
                ? <CheckIcon sx={{ fontSize: 20 }} />
                : <Typography sx={{ fontSize: '0.85rem', fontWeight: 700, lineHeight: 1 }}>{stepNum}</Typography>
              }
            </Box>

            <Typography
              variant="h5"
              component="h1"
              sx={{ fontWeight: 700, color: 'var(--fg-strong)', lineHeight: 1.2 }}
            >
              {title}
            </Typography>
          </Stack>

          {/* AI Suggest + Complete toggle */}
          <Stack direction="row" spacing={1} alignItems="center" flexShrink={0}>
            {onAISuggest && !readOnly && (
              <Button
                size="small"
                onClick={handleAISuggest}
                disabled={aiLoading}
                startIcon={aiLoading ? <CircularProgress size={14} /> : <AIIcon />}
                sx={{
                  borderColor: 'rgba(168,85,247,0.4)',
                  color: 'var(--ai-accent)',
                  background: 'linear-gradient(135deg, rgba(168,85,247,0.08), rgba(99,102,241,0.08))',
                  border: '1px solid rgba(168,85,247,0.3)',
                  '&:hover': {
                    borderColor: '#a855f7',
                    background: 'linear-gradient(135deg, rgba(168,85,247,0.18), rgba(99,102,241,0.18))',
                  },
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  textTransform: 'none',
                  px: 1.5,
                  height: 32
                }}
              >
                {aiLoading ? t('ai.generating') : t('ai.suggest')}
              </Button>
            )}

            {!readOnly && (
            <Chip
              icon={isComplete ? <CheckIcon /> : undefined}
              label={isComplete ? t('status.completed') : t('actions.markComplete')}
              onClick={canComplete ? onToggleComplete : undefined}
              disabled={!canComplete}
              sx={{
                flexShrink: 0,
                height: 32,
                fontWeight: 600,
                fontSize: '0.78rem',
                cursor: canComplete ? 'pointer' : 'default',
                bgcolor: isComplete
                  ? 'rgba(34,197,94,0.15)'
                  : 'rgba(99,102,241,0.1)',
                color: isComplete ? 'var(--status-green)' : 'var(--fg-muted)',
                border: `1px solid ${isComplete ? 'rgba(34,197,94,0.35)' : 'rgba(99,102,241,0.3)'}`,
                '& .MuiChip-icon': {
                  color: isComplete ? 'var(--status-green)' : 'inherit',
                  fontSize: 16
                },
                '&:hover': canComplete && !isComplete ? {
                  bgcolor: 'rgba(99,102,241,0.2)',
                  borderColor: 'rgba(99,102,241,0.5)'
                } : {},
                '&.Mui-disabled': {
                  opacity: 0.45
                }
              }}
            />
            )}
          </Stack>
        </Stack>

        {/* Intro description */}
        <Typography
          variant="body2"
          sx={{
            mt: 2,
            ml: { sm: '54px' },
            color: 'var(--fg-muted)',
            lineHeight: 1.65,
            maxWidth: 700
          }}
        >
          {intro}
        </Typography>

        {/* Requires info */}
        {!canComplete && requireMessage && (
          <Stack
            direction="row"
            alignItems="center"
            spacing={0.75}
            sx={{ mt: 2, ml: { sm: '54px' } }}
          >
            <InfoIcon sx={{ fontSize: 14, color: 'rgba(99,102,241,0.7)', flexShrink: 0 }} />
            <Typography
              variant="caption"
              sx={{ color: 'var(--fg-faint)', lineHeight: 1.4 }}
            >
              {requireMessage}
            </Typography>
          </Stack>
        )}
      </Box>
    </Box>
  );
}
